import dayjs from 'dayjs';
import Link from 'next/link';
import { useState, useEffect } from 'react';
import { combineLatest } from 'rxjs';

import styles from '../../../styles/pages/index.module.scss';
import { getCmsAllPaths } from '../../api/Articles';
import { getLandingPage, getRedirections } from '../../api/cms';
import CustomMetaHead from '../../components/CustomMetaHead';
import Footer from '../../components/Footer/Footer';
import Header from '../../components/Header/Header';
import LandingPageLayout from '../../components/LandingPageLayout';
import pageStyles from '../../components/LandingPageLayout.module.scss';
import LandingPageRecipes from '../../components/LandingPageRecipes';
import Navigation from '../../components/Navigation/Navigation';
import META_DATA from '../../constants/metaData';
import { OFFERS } from '../../constants/routes';
import CategoryStore from '../../store/categories';
import DeliveryMode from '../../store/deliveryMode';
import ProductsCollection from '../../store/productsCollection';
import { getOrFetchFooterLinks } from '../../utils/footerLinks';
import { createBreadcrumbJson, getBreadcrumbTwoStep } from '../../utils/getBreadcrumb';
import { getOrFetchNavigationLinks } from '../../utils/navigationLinks';
import { splitProductsIntoCategories } from '../../utils/products';
import { serverSideOffersFetchingFromFile } from '../../utils/server-side/offers';

export default function LandingPage({
  cmsData,
  slug,
  redirections,
  offers,
  featuredSKUs,
  featuredOffers,
  footerLinks,
  navigation,
  meta,
}) {
  const [productsShowing, setProductsShowing] = useState();
  const [categoriesShowing, setCategoriesShowing] = useState();
  const [offersShowing, setOffersShowing] = useState();
  const isDivideToCategories = cmsData.fields.sections[0].divideCategories;

  const breadcrumbData = getBreadcrumbTwoStep(slug);
  const breadcrumb = createBreadcrumbJson(breadcrumbData);

  useEffect(() => {
    const categoriesAndProducts$ = combineLatest([CategoryStore.subject, ProductsCollection.subject]).subscribe(
      categoriesAndProducts => {
        const [_categories, _productCollections] = categoriesAndProducts;
        if (!_categories.length || !_productCollections) return;
        const service = DeliveryMode.getValue();
        const collectionType = DeliveryMode.getCollectionAsNumber(service);
        const _allProductsIncollections = ProductsCollection.getProductsForCollections(collectionType);
        if (!_allProductsIncollections?.length) return;
        const _productsShowing = featuredSKUs
          // eslint-disable-next-line max-len
          ?.map(sku => _allProductsIncollections.find(product => product.sku === sku && product.enabled && product.available)
          )
          ?.filter(p => p);
        if (!isDivideToCategories) {
          setProductsShowing(_productsShowing);
          return;
        }
        const [productsOrderSorted, categoriesArrayFiltered] = splitProductsIntoCategories(
          _productsShowing,
          _categories
        );
        setCategoriesShowing(categoriesArrayFiltered);
        setProductsShowing(productsOrderSorted);
      }
    );

    const deliveryMode$ = DeliveryMode.subscribe(service => {
      const collectionType = DeliveryMode.getCollectionAsNumber(service);
      const mainCollectionType = collectionType.find(c => c === process.env.defaultCollectionType || c === '751');
      const mainCollectionTypeNumber = parseInt(mainCollectionType);
      const _offersShowing = featuredOffers
        ?.map(friendlyId => offers.find(offer => offer.friendlyId === friendlyId))
        ?.filter(o => o?.collectionTypes.includes(mainCollectionTypeNumber));
      setOffersShowing(_offersShowing);
    });

    return () => {
      deliveryMode$.unsubscribe();
      categoriesAndProducts$.unsubscribe();
    };
  }, []);
  return (
    <>
      <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(breadcrumb) }} />
      <CustomMetaHead meta={meta} />

      <div className={styles.headerContainer}>
        <Header navigation={navigation} />
        <Navigation navigation={navigation} />
      </div>
      <LandingPageLayout
        cmsData={cmsData}
        products={productsShowing}
        showProductCategories={isDivideToCategories}
        categoriesShowing={categoriesShowing}
        offersShowing={offersShowing}
      />
      <LandingPageRecipes cmsData={cmsData} redirections={redirections} />
      <div className={pageStyles.linkContainer}>
        <Link href={OFFERS}>
          <a className={pageStyles.link}>Επιστροφή στις κατηγορίες</a>
        </Link>
      </div>

      <Footer footerLinks={footerLinks} />
    </>
  );
}

export async function getStaticPaths() {
  const contentType = 'landingPage';
  const paths = await getCmsAllPaths(contentType);
  return { paths, fallback: false };
}

export async function getStaticProps(context) {
  const { slug } = context.params;
  const [pageData, redirectionsData, footerLinks, { data: navigation }] = await Promise.all([
    getLandingPage(`${slug}`),
    getRedirections(),
    getOrFetchFooterLinks(),
    getOrFetchNavigationLinks(),
  ]);

  const cmsData = pageData?.data?.[0];
  const redirections = redirectionsData?.data;
  const offersAll = serverSideOffersFetchingFromFile();
  const section = cmsData?.fields?.sections?.[0];
  const pageTitle = section?.heading || cmsData?.title || '';
  const description = `${pageTitle} - Δείτε τις νέες προσφορές! Τα αγαπημένα σας προϊόντα σε super τιμές.`;
  const featuredSKUs = (section?.csv || '').split(',').map(sku => sku.trim());

  const featuredOffers = (section?.csvOffers || '').split(',').map(id => {
    if (!Number(id)) return 0;
    return parseInt(id.trim());
  });

  const nowUnix = dayjs().unix();
  const offers = offersAll.filter(
    offer => offer.enabled && offer.available && offer.beginDate < nowUnix && offer.endDate > nowUnix
  );

  const meta = {
    ...META_DATA[process.env.appName].featured,
    title: pageTitle,
    description,
    url: `${process.env.domain}/featured/${slug}/`,
  };

  return {
    props: {
      cmsData,
      slug: `featured/${slug}`,
      redirections,
      offers,
      featuredSKUs,
      featuredOffers,
      footerLinks,
      navigation,
      meta,
    },
  };
}
